import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LabelTemplate } from '@/types/label';
import { usePresets } from '@/hooks/usePresets';
import {
  Package,
  Tag,
  QrCode,
  Barcode,
  FileText,
  Truck,
  Layers,
  Loader2,
} from 'lucide-react';

export interface TemplatePresetsProps {
  onSelectPreset: (template: LabelTemplate) => void;
  filterIds?: string[];
}

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  package: Package,
  tag: Tag,
  qrcode: QrCode,
  barcode: Barcode,
  filetext: FileText,
  truck: Truck,
  layers: Layers,
};

export const TemplatePresets: React.FC<TemplatePresetsProps> = ({
  onSelectPreset,
  filterIds,
}) => {
  const { activePresets, loading, createTemplate } = usePresets();
  const filteredPresets = filterIds ? activePresets.filter(p => filterIds.includes(p.id)) : activePresets;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-10 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading presets…
      </div>
    );
  }

  if (filteredPresets.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        No presets are currently available.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto max-w-fit mx-auto">
      <div className="grid grid-rows-2 grid-flow-col gap-3 p-4 auto-cols-[11rem]">
        {filteredPresets.map((preset) => {
          const Icon = ICONS[preset.icon?.toLowerCase()] || Package;
          return (
            <Card
              key={preset.id}
              className="cursor-pointer hover:border-primary transition-colors shrink-0"
              onClick={() => onSelectPreset(createTemplate(preset))}
            >
              <CardHeader className="p-3 pb-1">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-primary shrink-0" />
                  <CardTitle className="text-xs leading-tight">{preset.name}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-1">
                <CardDescription className="text-[11px] line-clamp-2">
                  {preset.description}
                </CardDescription>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
